// referenced from confluence/next/packages/error-boundary/src/ErrorComponent.tsx

import React, { type ErrorInfo, Fragment, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';

import { getReactErrorAttributes } from '../../utils/error/error-utils';

import { AtlassianSupportLink } from './atlassian-support-link';
import { DevErrorDetail } from './dev-error-detail';
import { i18n } from './messages';

const ContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
});

const SectionMessageContainerStyles = xcss({
	maxWidth: '600px',
});

export type ErrorComponentProps = {
	error: Error;
	errorInfo: ErrorInfo;
};

export const ErrorComponent = ({ error, errorInfo }: ErrorComponentProps): React.JSX.Element => {
	const intl = useIntl();
	const [showDevErrorDetail, setShowDevErrorDetail] = useState(false);

	useEffect(() => {
		focusError();
	}, []);

	const focusError = () => {
		const error = document.querySelector(`#error-container`);
		return (error as HTMLElement)?.focus();
	};

	const title = intl.formatMessage(i18n.title);

	let errorDetail = <ErrorDetail {...{ error, errorInfo }} />;
	const actions: [
		{
			key: string;
			text: string;
			onClick: () => any;
			appearance: 'danger' | 'subtle';
			autoFocus?: boolean;
		},
	] = [
		{
			key: 'error-boundary.reload-page',
			text: intl.formatMessage(i18n.actionReloadPage),
			appearance: 'danger',
			autoFocus: true,
			onClick() {
				window.location.reload();
			},
		},
	];

	if (process.env.NODE_ENV !== 'production') {
		actions.push({
			key: 'error-boundary.show-dev-error-details',
			appearance: 'subtle',
			text: `${showDevErrorDetail ? 'Hide' : 'Show'} dev-only details`,
			onClick() {
				setShowDevErrorDetail(!showDevErrorDetail);
			},
		});
	}

	if (showDevErrorDetail) {
		errorDetail = (
			<Fragment>
				{errorDetail}
				<DevErrorDetail {...{ error, errorInfo }} />
			</Fragment>
		);
	}

	return (
		<Box xcss={ContainerStyles}>
			<Box
				xcss={SectionMessageContainerStyles}
				testId="error-container-with-error-details"
				id="error-container"
				tabIndex={0}
			>
				<SectionMessage
					appearance="error"
					actions={actions.map(({ onClick, key, text }) => (
						<SectionMessageAction onClick={onClick} key={key}>
							{text}
						</SectionMessageAction>
					))}
					title={title}
				>
					{errorDetail}
				</SectionMessage>
			</Box>
		</Box>
	);
};

function ErrorDetail(props: ErrorComponentProps) {
	const reactErrorAttributes = getReactErrorAttributes(props.error);
	const errorHashCode = reactErrorAttributes ? reactErrorAttributes.errorHashCode : undefined;

	return (
		<Fragment>
			<FormattedMessage
				{...i18n.description}
				tagName="p"
				values={{
					atlassianSupportLink: (
						<AtlassianSupportLink>
							<FormattedMessage {...i18n.supportLinkText} />
						</AtlassianSupportLink>
					),
				}}
			/>

			<ul>
				<li>
					<FormattedMessage {...i18n.url} />
				</li>
				{errorHashCode ? (
					<li>
						<FormattedMessage {...i18n.code} values={{ code: <b>{errorHashCode || ''}</b> }} />
					</li>
				) : null}
			</ul>
		</Fragment>
	);
}
